import { PUBLIC_API_BASE } from '#lib/publicUrls.ts';

export type PressureUnit = 'psig' | 'psia' | 'kPa' | 'bar';
export type TemperatureUnit = 'F' | 'C';
export type EnthalpyUnit = 'kJ_kg' | 'btu_lb';

export interface SideInput {
  pressure: number;
  pressure_unit: PressureUnit;
  temperature: number;
  temperature_unit: TemperatureUnit;
}

export interface CalculateRequest {
  fluid: string;
  low: SideInput;
  high: SideInput;
  atmospheric_kpa?: number;
  enthalpy_unit?: EnthalpyUnit;
  pressure_unit?: PressureUnit;
  temperature_unit?: TemperatureUnit;
}

export interface ChartPoint {
  label: string;
  p_display: number;
  h_display: number;
}

export interface SideResult {
  p_display: number;
  t_display: number;
  t_sat_display: number;
  h_display: number;
  superheat_display: number | null;
  subcooling_display: number | null;
  note: string | null;
  chart_point: ChartPoint;
  sat_chart_point: ChartPoint;
}

export interface CalculateResponse {
  fluid: string;
  warnings: string[];
  low: SideResult;
  high: SideResult;
  chart_points: ChartPoint[];
  units: {
    pressure: PressureUnit;
    temperature: TemperatureUnit;
    enthalpy: EnthalpyUnit;
  };
}

export interface DiagramPoint {
  p_display: number;
  h_display: number;
}

export interface DiagramResponse {
  fluid: string;
  liquid_line: DiagramPoint[];
  vapor_line: DiagramPoint[];
  critical: DiagramPoint;
  units: {
    pressure: PressureUnit;
    enthalpy: EnthalpyUnit;
  };
}

const JSON_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

async function parseError(resp: Response): Promise<string> {
  try {
    const data = await resp.json();
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) {
      return data.detail.map((d: { msg?: string }) => d.msg ?? String(d)).join(' ');
    }
  } catch {
    // ignore
  }
  return `Request failed (${resp.status})`;
}

export async function fetchFluids(): Promise<string[]> {
  const resp = await fetch(`${PUBLIC_API_BASE}/refrigerant/fluids`, {
    headers: { Accept: 'application/json' },
  });
  if (!resp.ok) throw new Error(await parseError(resp));
  const data = await resp.json();
  return data.fluids as string[];
}

export async function fetchDiagram(params: {
  fluid: string;
  points?: number;
  pressure_unit: PressureUnit;
  enthalpy_unit: EnthalpyUnit;
  atmospheric_kpa?: number;
}): Promise<DiagramResponse> {
  const qs = new URLSearchParams({
    fluid: params.fluid,
    points: String(params.points ?? 120),
    pressure_unit: params.pressure_unit,
    enthalpy_unit: params.enthalpy_unit,
    atmospheric_kpa: String(params.atmospheric_kpa ?? 101.325),
  });
  const resp = await fetch(`${PUBLIC_API_BASE}/refrigerant/diagram?${qs}`, {
    headers: { Accept: 'application/json' },
  });
  if (!resp.ok) throw new Error(await parseError(resp));
  return resp.json();
}

export async function calculateRefrigerant(
  body: CalculateRequest,
): Promise<CalculateResponse> {
  const resp = await fetch(`${PUBLIC_API_BASE}/refrigerant/calculate`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(await parseError(resp));
  return resp.json();
}
